'use client';

import { useTranslations } from 'next-intl';
import { useRef, useEffect, useState, useCallback } from 'react';

interface HoloCardProps {
  serial?: string | number | null;
  /** Play flip-in animation on mount (back → front reveal) */
  flipIn?: boolean;
  /** Show the card back-face (scanning state) */
  scanning?: boolean;
  /** Variant color overlay for different result types */
  variant?: 'default' | 'green' | 'blue' | 'amber' | 'red';
  /** Product name displayed on the card */
  productName?: string;
  /** Title text (e.g. "Verified Authentic") */
  title?: string;
}

export function HoloCard({ serial, flipIn, scanning, variant = 'default', productName, title }: HoloCardProps) {
  const t = useTranslations('public');

  if (scanning) {
    return <HoloCardBack />;
  }

  return (
    <div
      className={flipIn ? 'pub-flip-in' : undefined}
      style={{ perspective: 800, padding: '6px 0' }}
    >
      <HoloCardFront serial={serial} variant={variant} productName={productName} title={title} flipIn={flipIn} />
    </div>
  );
}

/** Front face — the certificate reveal */
function HoloCardFront({
  serial,
  variant,
  productName,
  title,
  flipIn,
}: {
  serial?: string | number | null;
  variant: 'default' | 'green' | 'blue' | 'amber' | 'red';
  productName?: string;
  title?: string;
  flipIn?: boolean;
}) {
  const t = useTranslations('public');
  const cardRef = useRef<HTMLDivElement>(null);
  const glossRef = useRef<HTMLDivElement>(null);
  const shimmerRef = useRef<HTMLDivElement>(null);
  const checkRef = useRef<SVGPolylineElement>(null);
  const [tiltReady, setTiltReady] = useState(!flipIn);

  const overlayMap = {
    default: 'transparent',
    green: 'rgba(34,197,94,0.08)',
    blue: 'rgba(59,130,246,0.08)',
    amber: 'rgba(245,158,11,0.1)',
    red: 'rgba(239,68,68,0.12)',
  };

  const statusColorMap = {
    default: 'rgba(255,255,255,0.85)',
    green: 'rgba(34,197,94,0.9)',
    blue: 'rgba(59,130,246,0.9)',
    amber: 'rgba(245,158,11,0.9)',
    red: 'rgba(239,68,68,0.9)',
  };

  const statusLabelMap = {
    default: t('verified.certificate'),
    green: t('verified.genuine'),
    blue: t('revisit.yours'),
    amber: t('warning.flagged'),
    red: t('invalid.title'),
  };

  const hasExtra = !!(title || productName);

  // Animate checkmark stroke after flip (or on mount if no flip)
  useEffect(() => {
    const el = checkRef.current;
    if (!el) return;
    const length = el.getTotalLength();
    el.style.strokeDasharray = `${length}`;
    el.style.strokeDashoffset = `${length}`;

    const delay = flipIn ? 1100 : 200;
    const timer = setTimeout(() => {
      el.style.transition = 'stroke-dashoffset 0.6s cubic-bezier(0.65, 0, 0.35, 1)';
      el.style.strokeDashoffset = '0';
    }, delay);

    return () => clearTimeout(timer);
  }, [flipIn]);

  // Shimmer sweep after flip lands
  useEffect(() => {
    if (!shimmerRef.current) return;
    const delay = flipIn ? 1000 : 100;
    const timer = setTimeout(() => {
      if (shimmerRef.current) {
        shimmerRef.current.style.animation = 'holo-shimmer-sweep 0.8s ease-out forwards';
      }
    }, delay);
    return () => clearTimeout(timer);
  }, [flipIn]);

  // Enable tilt after flip completes
  useEffect(() => {
    if (!flipIn) return;
    const timer = setTimeout(() => setTiltReady(true), 950);
    return () => clearTimeout(timer);
  }, [flipIn]);

  // Gyroscope / mouse tilt
  const applyTilt = useCallback((rx: number, ry: number) => {
    const card = cardRef.current;
    const gloss = glossRef.current;
    if (!card || !tiltReady) return;

    card.style.transform = `perspective(800px) rotateX(${rx}deg) rotateY(${ry}deg)`;

    if (gloss) {
      const gx = 50 + ry * 2.5;
      const gy = 50 - rx * 2.5;
      gloss.style.background = `radial-gradient(circle at ${gx}% ${gy}%, rgba(255,255,255,0.25) 0%, transparent 60%)`;
    }
  }, [tiltReady]);

  useEffect(() => {
    if (!tiltReady) return;

    let gyroActive = false;

    // Gyroscope — Android only (no permission API = not iOS)
    const hasGyro = typeof window !== 'undefined'
      && typeof window.DeviceOrientationEvent !== 'undefined'
      && !('requestPermission' in window.DeviceOrientationEvent);

    const handleOrientation = (e: DeviceOrientationEvent) => {
      if (e.gamma == null || e.beta == null) return;
      gyroActive = true;
      const ry = Math.max(-12, Math.min(12, e.gamma * 0.3));
      const rx = Math.max(-8, Math.min(8, (e.beta - 45) * -0.2));
      applyTilt(rx, ry);
    };

    if (hasGyro) {
      window.addEventListener('deviceorientation', handleOrientation);
    }

    const handleMouse = (e: MouseEvent) => {
      if (gyroActive) return;
      const card = cardRef.current;
      if (!card) return;
      const rect = card.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const mx = (e.clientX - cx) / (rect.width / 2);
      const my = (e.clientY - cy) / (rect.height / 2);
      const ry = Math.max(-12, Math.min(12, mx * 8));
      const rx = Math.max(-8, Math.min(8, my * -5));
      applyTilt(rx, ry);
    };

    const handleMouseLeave = () => {
      if (gyroActive) return;
      const card = cardRef.current;
      const gloss = glossRef.current;
      if (card) card.style.transform = 'perspective(800px) rotateX(0deg) rotateY(0deg)';
      if (gloss) gloss.style.background = 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.25) 0%, transparent 60%)';
    };

    window.addEventListener('mousemove', handleMouse);
    window.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      if (hasGyro) window.removeEventListener('deviceorientation', handleOrientation);
      window.removeEventListener('mousemove', handleMouse);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [tiltReady, applyTilt]);

  const cardHeight = hasExtra ? 170 : 130;

  return (
    <div style={{ height: cardHeight, position: 'relative' }}>
    <div
      ref={cardRef}
      className="relative flex flex-col justify-between"
      style={{
        position: 'absolute',
        inset: 0,
        borderRadius: 'var(--pub-card-radius)',
        background: 'var(--pub-holographic)',
        padding: '14px 16px',
        transition: 'transform 0.15s ease-out',
        willChange: 'transform',
        overflow: 'hidden',
      }}
    >
      {/* Gloss overlay — moves with tilt */}
      <div
        ref={glossRef}
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.25) 0%, transparent 60%)',
          transition: 'background 0.15s ease-out',
        }}
      />
      {/* Stripe pattern */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(255,255,255,0.03) 2px, rgba(255,255,255,0.03) 4px)',
        }}
      />
      {/* Shimmer sweep */}
      <div
        ref={shimmerRef}
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.35) 50%, transparent 60%)',
          backgroundSize: '200% 100%',
          backgroundPosition: '-100% 0',
          opacity: 0,
        }}
      />
      {/* Variant color tint */}
      {variant !== 'default' && (
        <div
          className="absolute inset-0"
          style={{ background: overlayMap[variant] }}
        />
      )}

      {/* Top row: status badge + brand */}
      <div className="relative z-10 flex justify-between items-start">
        <span
          style={{
            fontFamily: 'var(--font-pub-mono)',
            fontSize: 9,
            fontWeight: 600,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: statusColorMap[variant],
            background: 'rgba(0,0,0,0.3)',
            padding: '3px 8px',
            borderRadius: 6,
          }}
        >
          {statusLabelMap[variant]}
        </span>
        <span
          style={{
            fontFamily: 'var(--font-pub-display)',
            fontSize: 11,
            fontWeight: 800,
            color: 'rgba(255,255,255,0.8)',
            letterSpacing: '0.06em',
          }}
        >
          {t('brand')}.
        </span>
      </div>

      {/* Middle: title + product name */}
      {hasExtra && (
        <div className="relative z-10 flex items-center gap-2.5">
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2.5" width="18" height="18">
              <polyline ref={checkRef} points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div>
            {title && (
              <span
                style={{
                  display: 'block',
                  fontFamily: 'var(--font-pub-display)',
                  fontSize: 18,
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: 'rgba(255,255,255,0.95)',
                  lineHeight: 1.2,
                }}
              >
                {title}
              </span>
            )}
            {productName && (
              <span
                style={{
                  display: 'block',
                  fontFamily: 'var(--font-pub-display)',
                  fontSize: 12,
                  color: 'rgba(255,255,255,0.6)',
                  marginTop: 2,
                }}
              >
                {productName}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Bottom row: certificate label + serial */}
      <div className="relative z-10 flex justify-between items-end">
        <span
          style={{
            fontFamily: 'var(--font-pub-display)',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.85)',
          }}
        >
          {t('verified.certificate')}
        </span>
        {serial && (
          <span
            style={{
              fontFamily: 'var(--font-pub-mono)',
              fontSize: 9,
              color: 'rgba(255,255,255,0.65)',
              letterSpacing: '0.12em',
            }}
          >
            #{serial}
          </span>
        )}
      </div>
    </div>
    </div>
  );
}

/** Back face — shown during scanning with animated holographic shimmer */
function HoloCardBack() {
  const t = useTranslations('public');

  return (
    <div
      className="pub-holo-scanning relative overflow-hidden flex flex-col items-center justify-center"
      style={{
        height: 130,
        borderRadius: 'var(--pub-card-radius)',
        padding: '14px 16px',
      }}
    >
      {/* Stripe pattern */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(255,255,255,0.04) 2px, rgba(255,255,255,0.04) 4px)',
        }}
      />
      {/* Dark overlay for readability */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(0,0,0,0.25)' }}
      />

      {/* Scan indicator */}
      <div className="relative z-10 flex flex-col items-center gap-2">
        {/* Spinner */}
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            border: '2px solid rgba(255,255,255,0.15)',
            borderTopColor: 'rgba(255,255,255,0.8)',
            animation: 'pub-spin 1s linear infinite',
          }}
        />
        <span
          style={{
            fontFamily: 'var(--font-pub-mono)',
            fontSize: 9,
            fontWeight: 600,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.75)',
          }}
        >
          {t('scanning.label')}
        </span>
      </div>

      {/* Brand watermark */}
      <span
        className="absolute z-10"
        style={{
          bottom: 12,
          right: 14,
          fontFamily: 'var(--font-pub-display)',
          fontSize: 10,
          fontWeight: 800,
          color: 'rgba(255,255,255,0.3)',
          letterSpacing: '0.06em',
        }}
      >
        {t('brand')}.
      </span>
    </div>
  );
}
